import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { delayedRetryHttp } from './shared/delayed-retry';
import {
    BcmBreakdownsResponse,
    BcmChartsResponse,
    BcmDashboardResponse,
    BcmFilter,
    BcmFilterOptionsResponse,
    BcmSummaryResponse
} from '../models/bcm-dashboard.model';

export const BCM_REQUEST_TIMEOUT_MS = 120000;

@Injectable({
    providedIn: 'root'
})
export class BcmDashboardService {
    private readonly baseUrl: string;
    private readonly api: string = 'api/BcmDashboard';

    constructor(private httpClient: HttpClient, @Inject('BASE_URL') baseUrl: string) {
        this.baseUrl = baseUrl + this.api;
    }

    private headers(encryptedUser: string): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + encryptedUser
        });
    }

    getFilterOptions(encryptedUser: string, filter: BcmFilter): Observable<BcmFilterOptionsResponse> {
        let params = new HttpParams()
            .set('period', String(filter.period))
            .set('useCustomRange', String(filter.useCustomRange));

        if (filter.fromDate) { params = params.set('fromDate', filter.fromDate); }
        if (filter.toDate) { params = params.set('toDate', filter.toDate); }

        return this.httpClient
            .get<BcmFilterOptionsResponse>(`${this.baseUrl}/FilterOptions`,
                { headers: this.headers(encryptedUser), params: params })
            .pipe(delayedRetryHttp(), timeout(BCM_REQUEST_TIMEOUT_MS));
    }

    getSummary(encryptedUser: string, filter: BcmFilter): Observable<BcmSummaryResponse> {
        return this.post<BcmSummaryResponse>('Summary', encryptedUser, filter);
    }

    getBreakdowns(encryptedUser: string, filter: BcmFilter): Observable<BcmBreakdownsResponse> {
        return this.post<BcmBreakdownsResponse>('Breakdowns', encryptedUser, filter);
    }

    getCharts(encryptedUser: string, filter: BcmFilter): Observable<BcmChartsResponse> {
        return this.post<BcmChartsResponse>('Charts', encryptedUser, filter);
    }

    getDashboard(encryptedUser: string, filter: BcmFilter): Observable<BcmDashboardResponse> {
        return this.post<BcmDashboardResponse>('Dashboard', encryptedUser, filter);
    }

    refresh(encryptedUser: string): Observable<unknown> {
        return this.httpClient
            .get(`${this.baseUrl}/Refresh`, { headers: this.headers(encryptedUser) })
            .pipe(timeout(BCM_REQUEST_TIMEOUT_MS));
    }

    private post<T>(action: string, encryptedUser: string, filter: BcmFilter): Observable<T> {
        return this.httpClient
            .post<T>(`${this.baseUrl}/${action}`, filter, { headers: this.headers(encryptedUser) })
            .pipe(delayedRetryHttp(), timeout(BCM_REQUEST_TIMEOUT_MS));
    }
}
